import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatPHP, formatDate } from "@/lib/format";
import { CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function OrderConfirmation() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${reference}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
  }, [reference]);

  if (order === null) {
    return <div className="min-h-[60vh] grid place-items-center text-msr-slate" data-testid="order-loading">Loading order...</div>;
  }
  if (order === false) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="order-not-found">
        <div className="text-center">
          <h1 className="font-heading font-extrabold text-3xl mb-3">Order not found</h1>
          <Link to="/shop" className="btn-msr-outline">Back to Shop</Link>
        </div>
      </div>
    );
  }

  const copyRef = () => {
    navigator.clipboard.writeText(order.reference);
    toast.success("Reference copied");
  };

  return (
    <div data-testid="order-confirmation-page">
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-14 max-w-3xl">
          <div className="h-14 w-14 grid place-items-center bg-msr-green text-white mb-5">
            <CheckCircle2 size={28} />
          </div>
          <div className="overline mb-2">Order Received</div>
          <h1 className="font-heading font-extrabold text-3xl lg:text-5xl tracking-tight" data-testid="order-title">
            Thank you, {order.full_name.split(" ")[0]}.
          </h1>
          <p className="text-msr-slate mt-4 leading-relaxed">
            We have received your order. Our team will reach out shortly with payment instructions and next steps.
          </p>

          <div className="msr-card border-t-4 border-t-msr-blue mt-8" data-testid="order-card">
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="overline mb-1.5">Reference</div>
                <button onClick={copyRef} className="font-heading font-extrabold text-xl text-msr-blue inline-flex items-center gap-2" data-testid="order-reference">
                  {order.reference} <Copy size={14} />
                </button>
              </div>
              <div>
                <div className="overline mb-1.5">Placed on</div>
                <div className="font-semibold">{formatDate(order.created_at)}</div>
              </div>
              <div>
                <div className="overline mb-1.5">Payment method</div>
                <div className="font-semibold capitalize">{order.payment_method.replace("_", " ")}</div>
              </div>
              <div>
                <div className="overline mb-1.5">Status</div>
                <span className="badge-soft-blue">{order.status}</span>
              </div>
            </div>
            <div className="border-t border-msr-line pt-5">
              <div className="overline mb-3">Items</div>
              <div className="space-y-2">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-msr-line/60" data-testid={`order-item-${idx}`}>
                    <div>
                      <div className="font-semibold">{i.name}</div>
                      <div className="text-xs text-msr-slate">SKU: {i.sku} · Qty: {i.quantity}</div>
                    </div>
                    <div className="font-heading font-bold">{formatPHP(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-5 pt-4 border-t border-msr-line">
                <span className="font-heading font-extrabold">Total</span>
                <span className="font-heading font-extrabold text-2xl text-msr-blue" data-testid="order-total">{formatPHP(order.total)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/shop" className="btn-msr-primary">Continue Shopping</Link>
            <Link to="/" className="btn-msr-outline">Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
